import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';

import {
  injectNgControl,
  NoopValueAccessorDirective,
} from '@shared/directives/noop-value-accessor.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { TypedNgChanges } from '@shared/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [NoopValueAccessorDirective],
  imports: [NgSelectModule, ReactiveFormsModule, TooltipDirective],
  selector: 'aux-vendor-dropdown',
  styleUrl: './vendor-dropdown.component.scss',
  template: `
    <ng-select
      #select
      [style.width]="width + 'px'"
      [clearable]="false"
      [formControl]="ngControl.control"
      [placeholder]="placeholder()"
      bindLabel="name"
      bindValue="id"
      [items]="options"
      [loading]="loading"
      class="text-base"
      [auxTooltip]="
        (lbl?.nativeElement?.offsetWidth || 0) < (lbl?.nativeElement?.scrollWidth || 0) &&
        !select.isOpen
          ? $any(select.selectedItems[0])?.label || ''
          : ''
      "
      [auxTooltipPositions]="selectedOptionTooltipPositions"
    >
      <ng-template let-item="item" ng-option-tmp>
        <div
          class="option-wrapper block truncate"
          #text
          [auxTooltip]="text.offsetWidth < text.scrollWidth ? item.name : ''"
          [auxTooltipPositions]="optionTooltipPositions"
          (click)="clickOption($event, item.id)"
        >
          {{ item.name }}
        </div>
      </ng-template>
      <ng-template let-item="item" ng-label-tmp>
        <div class="block truncate" #lbl>
          {{ item.name }}
        </div>
      </ng-template>
    </ng-select>
  `,
})
export class VendorDropdownComponent implements AfterViewInit, OnChanges {
  private changeDetector = inject(ChangeDetectorRef);

  ngControl = injectNgControl();

  placeholder = input<string>('');

  @Input() appendTo = '';
  @Input() beforeSelect?: (nextValue: null | string) => boolean | Promise<boolean>;
  @ViewChild('lbl') lbl?: ElementRef<HTMLDivElement>;
  @Input() loading = false;
  options: {
    id: string;
    name: string;
  }[] = [];
  // eslint-disable-next-line
  @Output() onChange = new EventEmitter<string>();
  optionTooltipPositions: ConnectedPosition[] = [
    {
      offsetX: -10,
      offsetY: 8,
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];
  @ViewChild('select') select?: NgSelectComponent;
  selectedOptionTooltipPositions: ConnectedPosition[] = [
    {
      offsetX: 0,
      offsetY: 0,
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];
  @Input() showAllOption = false;
  @Input() vendors: {
    id: string;
    name: string;
  }[] = [];
  @Input() width = 350;

  ngOnChanges(changes: TypedNgChanges<VendorDropdownComponent>) {
    const vendors = changes.vendors?.currentValue || this.vendors;
    const showAllOption = changes.showAllOption?.currentValue || this.showAllOption;

    this.options =
      vendors.length > 1 && showAllOption ? [{ id: '', name: 'All' }, ...vendors] : vendors;

    this.changeDetector.markForCheck();
  }

  ngAfterViewInit(): void {
    // after render get the scrollWidth and offsetWidth
    setTimeout(() => {
      this.changeDetector.markForCheck();
    }, 0);
  }

  async clickOption(event: MouseEvent, value: string) {
    event.preventDefault();
    event.stopPropagation();

    if (this.beforeSelect && !(await this.beforeSelect(value))) {
      return;
    }

    this.ngControl.control.setValue(value);
    this.onChange.emit(value);

    if (this.select) {
      this.select.changeEvent?.emit(value);
      this.select.isOpen = false;
      this.select.closeEvent?.emit(true);
    }
  }
}
